import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { PartnerPageLayout } from "../../components/PartnerPageLayout";
import { PageLoadingSpinner } from "../../components/PageLoadingSpinner";
import { RoyaltySetupCard } from "../../components/RoyaltySetupCard";
import { partnerSectionLinks, xboxOnboardSiteLocationPrefix } from "../../constants";
import { useRequireAuth } from "../../constants/urlsClientSide";
import { useLocalizedCountriesOrRegionsList } from "../../helpers/countriesOrRegionsList";
import { PartnerAccessLevel, usePartnerRouteGuard } from "../../helpers/usePartnerRouteGuard";
import { useStrings } from "../../localization/localizationHelpers";
import type { PartnerOverviewResponse } from "../../models/partnerOverview";
import { hasAttemptedPartnerCenterEnrollment } from "../../models/studio";
import { getXboxOnboardData } from "../../redux/selectors/xboxOnboardSelectors";
import { getAvatarClass, getMemberInitials } from "../AdminPage/avatarHelpers";
import { PartnerNdaCard } from "../PartnerHomePage/components/PartnerNdaCard";
import { PartnerStudioCard } from "../PartnerHomePage/components/PartnerStudioCard";
import * as styles from "./PartnerOverviewPage.module.css";

const overviewDataUrl = `${xboxOnboardSiteLocationPrefix}/overview/api/data`;
const maxPeoplePreview = 24;
const maxAdministratorsPreview = 5;

export const PartnerOverviewPage = () => {
  const isPageReady = usePartnerRouteGuard(PartnerAccessLevel.NdaSignedOrAaa);
  const isSignedIn = useRequireAuth();
  const studioData = useSelector(getXboxOnboardData);
  const countriesOrRegions = useLocalizedCountriesOrRegionsList(
    studioData?.countriesOrRegions,
    false
  );
  const isHubAdmin = studioData?.isHubAdmin === true;
  const [overviewData, setOverviewData] = useState<PartnerOverviewResponse | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [
    pageTitle,
    loadingLabel,
    loadErrorLabel,
    peopleTitle,
    peopleCountLabel,
    viewAllLabel,
    administratorsTitle,
    administratorsCountLabel,
    noAdministratorsLabel,
    idXboxTitle,
    idXboxDescription,
    idXboxConceptRequired,
    joinIdXboxLabel,
    viewIdXboxApplicationLabel,
  ] = useStrings(
    "partnerOverviewPageTitle",
    "partnerOverviewLoadingStatus",
    "partnerOverviewLoadError",
    "partnerPeopleLabel",
    "partnerOverviewPeopleCount",
    "partnerOverviewViewAllLabel",
    "partnerOverviewAdministratorsTitle",
    "partnerOverviewAdministratorsCount",
    "partnerOverviewNoAdministrators",
    "partnerSettingsIdXboxTitle",
    "partnerOverviewIdXboxDescription",
    "partnerOverviewIdXboxConceptRequired",
    "partnerOverviewJoinIdXboxLabel",
    "partnerOverviewViewIdXboxApplicationLabel"
  );

  useEffect(() => {
    if (!isPageReady || !isSignedIn) {
      return;
    }

    let active = true;
    setLoadFailed(false);
    fetch(overviewDataUrl)
      .then(async response => {
        if (!response.ok) {
          throw new Error(`Failed to load partner overview: ${response.status}`);
        }
        return (await response.json()) as PartnerOverviewResponse;
      })
      .then(response => {
        if (active) {
          setOverviewData(response);
        }
      })
      .catch(() => {
        if (active) {
          setLoadFailed(true);
        }
      });

    return () => {
      active = false;
    };
  }, [isPageReady, isSignedIn]);

  if (!isPageReady || !isSignedIn || !studioData || (!overviewData && !loadFailed)) {
    return (
      <PartnerPageLayout breadcrumbLabel={pageTitle} wide>
        <div role="status" aria-live="polite" aria-label={loadingLabel} aria-busy="true">
          <PageLoadingSpinner />
        </div>
      </PartnerPageLayout>
    );
  }

  if (!overviewData || loadFailed) {
    return (
      <PartnerPageLayout breadcrumbLabel={pageTitle} wide>
        <p className={styles.error} role="alert">
          {loadErrorLabel}
        </p>
      </PartnerPageLayout>
    );
  }

  const country =
    countriesOrRegions.find(option => option.value === studioData.contact.countryOrRegion)?.label ??
    studioData.contact.countryOrRegion;
  const regionLine = [studioData.contact.stateRegion, studioData.contact.postalCode]
    .filter(Boolean)
    .join(" ");
  const cityLine = [studioData.contact.city, regionLine].filter(Boolean).join(", ");
  const addressLines = [
    studioData.contact.addressLine1,
    studioData.contact.addressLine2,
    cityLine,
    country,
  ].filter(Boolean);
  const peoplePath =
    studioData.hubPermissions.canAccessAdministratorSettings &&
    hasAttemptedPartnerCenterEnrollment(studioData.partnerCenterEnrollmentStatus)
      ? partnerSectionLinks.admin.to
      : partnerSectionLinks.members.to;
  const isLegalEntityNameChangeNdaRecipient =
    (studioData.legalEntityNameChange?.isApprovedAwaitingNda ||
      studioData.legalEntityNameChange?.hasNdaSent) &&
    studioData.legalEntityNameChange.ndaRecipientEmail?.trim().toLowerCase() ===
      studioData.contact.email.trim().toLowerCase();
  const legalEntityNameChange =
    studioData.hubPermissions.canAccessAdministratorSettings || isLegalEntityNameChangeNdaRecipient
      ? studioData.legalEntityNameChange
      : undefined;
  return (
    <PartnerPageLayout breadcrumbLabel={pageTitle} wide>
      <div className={styles.layout}>
        <div className={styles.mainColumn}>
          {legalEntityNameChange?.hasNdaSent ? (
            <PartnerNdaCard
              contactEmail={studioData.contact.email}
              ndaSentDate={studioData.ndaSentDate}
              legalEntityNameChange={legalEntityNameChange}
              managePath={partnerSectionLinks.overview.to}
              showAgreementsOption={false}
              showDevReadinessGuideOption={false}
              showSettingsOption={studioData.hubPermissions.canEditStudioDetails}
              settingsOnlyMenu
              onShowDevReadinessGuide={() => undefined}
            />
          ) : (
            <PartnerStudioCard
              studioName={studioData.studio.studioName}
              sellerId={studioData.sellerID}
              legalEntityName={studioData.studio.businessName}
              addressLines={addressLines}
              studioPhone={studioData.contact.phone}
              legalEntityNameChange={legalEntityNameChange}
              contactEmail={studioData.contact.email}
              isIdXboxApproved={studioData.isIdXboxApproved === true}
              isEnrollmentComplete={false}
              isEnrollmentEarlyComplete={false}
              showAgreementsOption={false}
              hasAttachedBanner={false}
              showDevReadinessGuideOption={false}
              showSettingsOption={studioData.hubPermissions.canEditStudioDetails}
              settingsOnlyMenu
              onShowDevReadinessGuide={() => undefined}
            />
          )}
          {studioData.accountType !== "aaa" && overviewData.royalty && studioData.isHubAdmin && (
            <RoyaltySetupCard {...overviewData.royalty} />
          )}
        </div>
        <aside className={styles.rightColumn}>
          {studioData.accountType === "id" &&
            !studioData.isIdXboxApproved &&
            studioData.hubPermissions.canAccessAdministratorSettings && (
              <section className={styles.card}>
                <h2>{idXboxTitle}</h2>
                <p>{idXboxDescription.replace("{0}", studioData.studio.studioName)}</p>
                {!studioData.hasSubmittedGameConcept && <p>{idXboxConceptRequired}</p>}
                <Link className={styles.actionLink} to={`${xboxOnboardSiteLocationPrefix}/id`}>
                  {studioData.hasStartedIdXboxApplication
                    ? viewIdXboxApplicationLabel
                    : joinIdXboxLabel}
                </Link>
              </section>
            )}
          <section className={styles.peopleCard}>
            {isHubAdmin && (
              <>
                <div className={styles.peopleHeader}>
                  <div>
                    <h2>{peopleTitle}</h2>
                    <p>{peopleCountLabel.replace("{0}", String(overviewData.people.total))}</p>
                  </div>
                </div>
                <ul className={styles.avatarList} aria-label={peopleTitle}>
                  {overviewData.people.members.slice(0, maxPeoplePreview).map(member => (
                    <li
                      className={`${styles.avatar} ${getAvatarClass(member.contactId)}`}
                      key={member.contactId}
                      title={`${member.firstName} ${member.lastName}`.trim()}
                    >
                      <span aria-hidden="true">{getMemberInitials({ ...member, email: "" })}</span>
                      <span className={styles.screenReaderOnly}>
                        {member.firstName} {member.lastName}
                      </span>
                    </li>
                  ))}
                </ul>
                <div className={styles.divider} />
              </>
            )}
            <h2>{administratorsTitle}</h2>
            <p>
              {administratorsCountLabel.replace(
                "{0}",
                String(overviewData.people.administrators.length)
              )}
            </p>
            {overviewData.people.administrators.length > 0 ? (
              <ul className={styles.administratorList} aria-label={administratorsTitle}>
                {overviewData.people.administrators
                  .slice(0, maxAdministratorsPreview)
                  .map(administrator => (
                    <li key={administrator.contactId}>
                      <div
                        className={`${styles.adminAvatar} ${getAvatarClass(
                          administrator.contactId
                        )}`}
                        aria-hidden="true"
                      >
                        {getMemberInitials(administrator)}
                      </div>
                      <div className={styles.administratorDetails}>
                        <span className={styles.administratorName}>
                          {administrator.firstName} {administrator.lastName}
                        </span>
                        {administrator.jobTitle && <span>{administrator.jobTitle}</span>}
                        <span>{administrator.email}</span>
                      </div>
                    </li>
                  ))}
              </ul>
            ) : (
              <p className={styles.empty}>{noAdministratorsLabel}</p>
            )}
            <div className={styles.peopleFooter}>
              <Link className={styles.actionLink} to={peoplePath}>
                {viewAllLabel}
              </Link>
            </div>
          </section>
        </aside>
      </div>
    </PartnerPageLayout>
  );
};
